import { EMPLOYEE_TEMP_LABEL } from "../constants";
import { processRow, processHeader, downloadCSV } from "../csvUtils";
import { padTime } from "../dateTimeFormatter";
import { BID_TYPE } from "./bidConstants";

export const generateExportedFilename = ({
  station,
  workgroup,
  bidType,
  effectiveStartDate,
  effectiveEndDate,
  schedMonth,
  schedYear,
}) => {
  let filename;
  let underScoredEffectiveStartDate, underScoredEffectiveEndDate;
  let underScoredWorkgroup = workgroup.split(" ").join("_");
  let underScoredBidType = bidType.split(" ").join("_");

  switch (bidType) {
    case BID_TYPE.SHIFT:
      underScoredEffectiveStartDate = `${padTime(effectiveStartDate.month)}_${padTime(effectiveStartDate.day)}_${
        effectiveStartDate.year
      }`;
      underScoredEffectiveEndDate = `${padTime(effectiveEndDate.month)}_${padTime(effectiveEndDate.day)}_${
        effectiveEndDate.year
      }`;
      filename = `STARS_${station}_${underScoredWorkgroup}_${underScoredBidType}_Results_${underScoredEffectiveStartDate}_to_${underScoredEffectiveEndDate}.csv`;
      break;
    case BID_TYPE.FREEDAY:
    case BID_TYPE.FTO:
    case BID_TYPE.TWU_DAT_FTO:
    case BID_TYPE.IAM_DAT:
    case BID_TYPE.NC_PTO_FTO:
    case BID_TYPE.NC_PTO_GOC_HOL:
      filename = `STARS_${station}_${underScoredWorkgroup}_${underScoredBidType}_Results_${schedMonth}_${schedYear}.csv`;
      break;
    default:
      console.error("Unknown bidType");
      filename = "";
  }

  return filename;
};

export function exportToCsv({ filename, rows, bidType }) {
  let csvFile = "";
  let scrubFunction;

  switch (bidType) {
    case BID_TYPE.SHIFT:
      scrubFunction = scrubShiftBidAwardRowForExport;
      break;
    case BID_TYPE.FREEDAY:
      scrubFunction = scrubTWUFreedayBidAwardRowForExport;
      break;
    case BID_TYPE.FTO:
      scrubFunction = scrubIAMFTOBidAwardRowForExport;
      break;

    case BID_TYPE.IAM_DAT:
      scrubFunction = scrubIAMDATBidAwardRowForExport;
      break;
    case BID_TYPE.TWU_DAT_FTO:
    case BID_TYPE.NC_PTO_FTO:
    case BID_TYPE.NC_PTO_GOC_HOL:
      scrubFunction = scrubBidAwardRowWithDifferentTimeoffsForExport;
      break;
    default:
      console.error("Unknown bidType");
  }

  for (let i = 0; i < rows.length; i++) {
    const currentRowToProcess = scrubFunction(rows[i]);
    if (i === 0) {
      csvFile += processHeader(currentRowToProcess);
    }
    csvFile += processRow(currentRowToProcess);
  }

  downloadCSV(csvFile, filename);
}

const scrubShiftBidAwardRowForExport = (awardedBidRow) => {
  let scrubbedShiftBidRow = {
    "Bid Line": awardedBidRow["lineNumber"],
    EID: awardedBidRow["awardedEid"] ? awardedBidRow["awardedEid"] : "-",
    Name:
      awardedBidRow["awardedLastName"] && awardedBidRow["awardedShortName"]
        ? `${awardedBidRow["awardedLastName"]},${awardedBidRow["awardedShortName"]}`
        : "-",
    "Bid Group Rank": awardedBidRow["awardedRank"] ? awardedBidRow["awardedRank"] : "-",
    "FT/PT": awardedBidRow["ftpt"],
    "Award Type": awardedBidRow["awardType"] ? awardedBidRow["awardType"] : "-",
    "Shift Differential": awardedBidRow["shiftDiff"] ? awardedBidRow["shiftDiff"] : "-",
  };

  for (const shift of awardedBidRow["shifts"]) {
    scrubbedShiftBidRow[`${shift.day} Shift`] = shift.isOff ? "Off" : `${shift.start}-${shift.end}`;
    scrubbedShiftBidRow[`${shift.day} Position`] = shift.isOff ? "" : shift.position;
    scrubbedShiftBidRow[`${shift.day} Assignment`] = shift.isOff ? "" : shift.assignment;
  }
  return scrubbedShiftBidRow;
};

const scrubTWUFreedayBidAwardRowForExport = (awardedBidRow) => {
  const scrubbedFreedayBidRow = {
    Date: awardedBidRow["date"],
    "Time Off Type": awardedBidRow["awardedTimeOffType"] ? awardedBidRow["awardedTimeOffType"] : "-",
    EID: awardedBidRow["eid"] ? awardedBidRow["eid"] : "-",
    Name:
      awardedBidRow["lastName"] && awardedBidRow["firstName"]
        ? awardedBidRow["isTemp"]
          ? `${awardedBidRow["lastName"]}, ${awardedBidRow["firstName"]} (${EMPLOYEE_TEMP_LABEL})`
          : `${awardedBidRow["lastName"]}, ${awardedBidRow["firstName"]}`
        : "-",
    "Bid Group Rank": awardedBidRow["awardedRank"] ? awardedBidRow["awardedRank"] : "-",
    "Award Type": awardedBidRow["awardType"] ? awardedBidRow["awardType"] : "-",
    Allotment: awardedBidRow["awardedAllotment"],
    "Standard/Relief": awardedBidRow["standardRelief"],
  };

  return scrubbedFreedayBidRow;
};

const scrubIAMFTOBidAwardRowForExport = (awardedBidRow) => {
  const scrubbedFtoBidRow = {
    Date: awardedBidRow["date"],
    "Time Off Type": awardedBidRow["awardedTimeOffType"] ? awardedBidRow["awardedTimeOffType"] : "-",
    EID: awardedBidRow["eid"] ? awardedBidRow["eid"] : "-",
    Name:
      awardedBidRow["lastName"] && awardedBidRow["firstName"]
        ? awardedBidRow["isTemp"]
          ? `${awardedBidRow["lastName"]}, ${awardedBidRow["firstName"]} (${EMPLOYEE_TEMP_LABEL})`
          : `${awardedBidRow["lastName"]}, ${awardedBidRow["firstName"]}`
        : "-",
    "Bid Group Rank": awardedBidRow["awardedRank"] ? awardedBidRow["awardedRank"] : "-",
    "Award Type": awardedBidRow["awardType"] ? awardedBidRow["awardType"] : "-",
  };

  return scrubbedFtoBidRow;
};

const scrubBidAwardRowWithDifferentTimeoffsForExport = (awardedBidRow) => {
  const scrubbedTwuDatFtoBidRow = {
    Date: awardedBidRow["date"],
    "Time Off Type": awardedBidRow["awardedDays"]
      ? awardedBidRow["awardedDays"]?.find((awardedDay) => awardedDay.date === awardedBidRow["date"]).timeOffType
      : "-",
    EID: awardedBidRow["eid"] ? awardedBidRow["eid"] : "-",
    Name:
      awardedBidRow["lastName"] && awardedBidRow["firstName"]
        ? awardedBidRow["isTemp"]
          ? `${awardedBidRow["lastName"]}, ${awardedBidRow["firstName"]} (${EMPLOYEE_TEMP_LABEL})`
          : `${awardedBidRow["lastName"]}, ${awardedBidRow["firstName"]}`
        : "-",
    "Bid Group Rank": awardedBidRow["awardedRank"] ? awardedBidRow["awardedRank"] : "-",
    "Award Type": awardedBidRow["awardType"] ? awardedBidRow["awardType"] : "-",
  };

  return scrubbedTwuDatFtoBidRow;
};

const scrubIAMDATBidAwardRowForExport = (awardedBidRow) => {
  const scrubbedIamDatBidRow = {
    Date: awardedBidRow["date"],
    "Time Off Type": awardedBidRow["awardedTimeOffType"] ? awardedBidRow["awardedTimeOffType"] : "-",
    EID: awardedBidRow["eid"] ? awardedBidRow["eid"] : "-",
    Name:
      awardedBidRow["lastName"] && awardedBidRow["firstName"]
        ? awardedBidRow["isTemp"]
          ? `${awardedBidRow["lastName"]}, ${awardedBidRow["firstName"]} (${EMPLOYEE_TEMP_LABEL})`
          : `${awardedBidRow["lastName"]}, ${awardedBidRow["firstName"]}`
        : "-",
    "Bid Group Rank": awardedBidRow["awardedRank"] ? awardedBidRow["awardedRank"] : "-",
    "Award Type": awardedBidRow["awardType"] ? awardedBidRow["awardType"] : "-",
  };

  return scrubbedIamDatBidRow;
};

const scrubNoBidIAORowForExport = (row) => {
  const scrubbedRow = {
    Bid: row["awardType"],
    Rank: row["rank"],
    EID: row["eid"],
    Name: `${row["lastName"]}, ${row["shortName"]}`,
    "FT/PT": row["ftpt"],
    "Assigned Line": row["awardedLine"] || "",
    "Phone #": row["phoneNumber"] || "-",
  };

  return scrubbedRow;
};

export function exportNoBidIAOToCsv({ filename, rows }) {
  let csvContent = "";

  for (let i = 0; i < rows.length; i++) {
    const currentRowToProcess = scrubNoBidIAORowForExport(rows[i]);
    if (i === 0) {
      csvContent += processHeader(currentRowToProcess);
    }
    csvContent += processRow(currentRowToProcess);
  }

  downloadCSV(csvContent, filename);
}

export const generateNoBidIAOExportFilename = ({
  station,
  workgroup,
  bidType,
  effectiveStartDate,
  effectiveEndDate,
  awardType,
}) => {
  let filename;
  const underScoredWorkgroup = workgroup.split(" ").join("_");
  const underScoredBidType = bidType.split(" ").join("_");
  const underScoredEffectiveStartDate = `${padTime(effectiveStartDate.month)}-${padTime(effectiveStartDate.day)}-${
    effectiveStartDate.year
  }`;
  const underScoredEffectiveEndDate = `${padTime(effectiveEndDate.month)}-${padTime(effectiveEndDate.day)}-${
    effectiveEndDate.year
  }`;
  filename = `STARS_${station}_${underScoredWorkgroup}_${underScoredBidType}_${awardType}_List_${underScoredEffectiveStartDate}_to_${underScoredEffectiveEndDate}.csv`;

  return filename;
};
